import { useMemo } from 'react';
import { UseTagsOptions, useItemsQueryForOffline } from 'src/queries/Item';
import { Employee, Company } from 'src/types';

export const useItemsForJob = (
  jobKey: string | undefined,
  authUser: Pick<Employee, 'companyKeys' | 'impersonatedCompanyKeys'>,
  company: Pick<Company, 'key' | 'partnerCompanyKeys'> | undefined,
  queryOptions?: UseTagsOptions
) => {
  // No review user can't see job items. In this case items query returns permission error.
  // NOTE: Employee.isNoReviewUser is not set for impersonated superadmin so it can't be used here.
  let hasPermissionToQueryTags = false;

  // We don't know if we can query the items until the company is loaded
  if (company) {
    const isNoReviewCompany = !!company.partnerCompanyKeys?.length;

    if (isNoReviewCompany) {
      // If the company has partners (i.e. no review company) we should check if the auth user has permission to query the items
      const authUserCompanies = authUser.impersonatedCompanyKeys?.length
        ? authUser.impersonatedCompanyKeys
        : authUser.companyKeys;

      // If the company is in the auth user companies (or impersonated by superadmin) then we can't query the items.
      // It means that the auth user is no review user.
      hasPermissionToQueryTags = !authUserCompanies?.includes(company.key);
    } else {
      // If the company is not a no review company, we can query the items
      hasPermissionToQueryTags = true;
    }
  }

  const queryResult = useItemsQueryForOffline(jobKey, {
    ...queryOptions,
    enabled: queryOptions?.enabled !== false && hasPermissionToQueryTags,
  });

  const tagsNoSeenPreviously = useMemo(() => {
    if (!company?.key) return undefined;

    // If company is loaded and no permission to query the items, return an empty array
    const items = hasPermissionToQueryTags ? queryResult.data : [];

    // Filter out items that have already been seen
    return items?.filter((item) => !item.seenPreviously);
  }, [hasPermissionToQueryTags, queryResult.data, company?.key]);

  return tagsNoSeenPreviously;
};
